/** Modal dialog: focus-trapped, Escape closes unless data would be lost. */
import { useEffect, useRef, type ReactNode } from "react";

import { IconButton } from "@/components/IconButton";

interface Props {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: "default" | "wide" | "wider";
  /** When true, a stray Escape or backdrop click cannot discard typed input. */
  dismissable?: boolean;
}

export function Dialog({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  width = "default",
  dismissable = true,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Focus the first control when the dialog opens, and only then.
  //
  // This used to depend on `onClose` as well. Callers pass an inline arrow, so
  // its identity changes on every parent render — and typing in a dialog field
  // re-renders the parent. The effect therefore re-ran on every keystroke and
  // moved focus back to the first control, which is the close button: a
  // message could only be written one character at a time, reselecting the
  // field in between.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.querySelector<HTMLElement>(
      "input, select, textarea, button, [tabindex]",
    )?.focus();
    return () => previous?.focus();
  }, [open]);

  // Escape, the focus trap and the scroll lock. These may be rebound freely —
  // none of them touch focus while the dialog is open.
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dismissable) {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [open, onClose, dismissable]);

  if (!open) return null;

  return (
    <div
      className="scrim"
      onMouseDown={(event) => {
        if (dismissable && event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`dialog${width === "wide" ? " dialog--wide" : width === "wider" ? " dialog--wider" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        ref={panelRef}
      >
        <div className="dialog__header">
          <div className="row" style={{ gap: "var(--space-4)", alignItems: "flex-start" }}>
            <div className="stack grow" style={{ gap: 4 }}>
              <h2 className="t-h3" id="dialog-title">
                {title}
              </h2>
              {description && <p className="t-body ink-secondary">{description}</p>}
            </div>
            <IconButton icon="x" label="Close" onClick={onClose} />
          </div>
        </div>
        <div className="dialog__body">{children}</div>
        {footer && <div className="dialog__footer">{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  destructive = false,
  busy = false,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--secondary" onClick={onClose}>
            Keep it
          </button>
          <button
            type="button"
            className={`btn ${destructive ? "btn--danger" : "btn--primary"}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Working…" : confirmLabel}
          </button>
        </>
      }
    >
      <div className="t-body">{message}</div>
    </Dialog>
  );
}
